/* The decision dialog, shared by every screen that takes one.
 *
 * It lived inside 24_ProjectApproval and was imported from there by the two pricing screens. That
 * worked until the CALCULATOR needed it too: importing 24 pulls in 15_SampleTracker behind it, and
 * the bundle came back with "Cannot access 'Ae' before initialization" — the whole page blank
 * (2026-09-11). A dialog three screens use is not a part of one of them.
 *
 * `spec.group` only needs { groupId, groupName }. The resubmit branch is Project Approval's own and
 * the pricing screens never ask for it.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEscapeClose from './useEscapeClose';

/* Approve, Reject and Resubmit all take a note. Approve used to be a bare click, which meant an
   approval "with a condition" had nowhere to be recorded. The Agree / Reject switch is kept inside
   the dialog (the reference UI Max's manager sent works that way), so a CEO who opens Approve and
   changes their mind does not have to close it and find the other button.
   The note is required for a rejection and optional otherwise.

   Sharing the component rather than copying its markup is what stops the screens drifting — the
   pricing screen had grown a separate Approve button and a separate Send back button, which is the
   layout this replaced. */
export const DECISION_MAX = 1000;

/* ONE SHARED EMPTY ARRAY. `waiting`, `decided` and the SKU list are rebuilt on every render, and
   `onMarks` fires an effect keyed on `decided` — so a fresh `[]` each time is an identity that
   never settles, and the callback sets the caller's state, which re-renders this dialog, which
   builds another `[]`. An infinite loop with nothing in the code that looks like one. */
const NO_SKUS = [];

export const DecisionDialog = ({
  spec, note, onNote, busy, selectedIds, onSwitch, onCancel, onConfirm, overlayClass,
  summary, onMarks,
}) => {
  // Esc = Cancel, except while the decision is being sent.
  useEscapeClose(onCancel, !busy);
  const { t } = useTranslation();
  const bulk = spec.mode === 'bulk';
  const isResubmit = spec.action === 'resubmit';
  const rejecting = spec.action === 'reject';

  /* ONE DECISION PER SKU — Jay and Max, 2026-09-18/19, out of Young rejecting PRJ-00185 whole when
     he wanted two of its five items. Jay's three outcomes (approve all, approve some, reject all)
     are not three modes to pick from: they are what the rows ADD UP TO, so the approver marks the
     items and the button reads back what he has decided before he presses it.

     A CROSS, not an empty box — Max: "ko thể hiện là ko tick mà là để biểu tượng x màu trắng nền
     màu bỏ á". A SKU missed while scrolling and a SKU refused on purpose would otherwise be the
     same row in the database, and nothing downstream could tell them apart.

     AND THE CROSS IS THE ONLY MARK — Max, 2026-09-20: "chỉ cần dấu X thui chứ". The approver
     arrived here by pressing Approve, so approving is what he came to do; a tick asked him to say
     it a second time on every line, and a dialog of two ticks answered nothing the button he
     pressed had not. What he needs is a way to name the EXCEPTIONS. So a SKU is approved unless it
     is crossed, and the confirm button reads back the sum — no row can be left unanswered, because
     there is no unanswered state to be in. Marked or not is still stored as Approved / Rejected per
     SKU, so nothing downstream sees a blank.

     Only on a single project's own decision. The bulk dialog decides many projects at once and
     keeps the Agree / Reject switch; the pricing screens pass no SKUs at all. */
  const skus = useMemo(
    () => ((!bulk && !isResubmit && Array.isArray(spec.group?.skuRows)) ? spec.group.skuRows : NO_SKUS),
    [bulk, isResubmit, spec.group],
  );
  /* WHAT CHANGED, PER ITEM — Max, 2026-09-21: "cần Manager Opinion cho từng SKU á". The resubmission
     carried one sentence for the project, so two items refused for different reasons came back with
     one explanation covering both and the approver could read it against neither.
     The items are the refused ones this resubmission is about: the ticked ones when the manager
     ticked, all of them otherwise, which is what the button itself does. */
  const backSkus = (isResubmit && Array.isArray(spec.group?.skuRows))
    ? spec.group.skuRows.filter((r) => r.approvalStatus === 'Rejected'
      && (!spec.group.resubmitSkus || spec.group.resubmitSkus.includes(r.projectId)))
    : [];
  /* Memoised for the same reason: it is a dependency of `decided`, which an effect reports to the
     caller. */
  const waiting = useMemo(
    () => skus.filter((s2) => (s2.approvalStatus || 'Pending') === 'Pending'),
    [skus],
  );
  const perSku = waiting.length > 0;
  const [marks, setMarks] = useState({});
  const [reasons, setReasons] = useState({});
  // The cross TOGGLES: pressed again the SKU goes back to being approved, which is the only way
  // out of a mis-click now that there is no second button to press.
  const toggle = (id) => setMarks((m) => ({ ...m, [id]: m[id] === 'Rejected' ? '' : 'Rejected' }));
  const decided = useMemo(() => waiting.map((s2) => ({
    projectId: s2.projectId,
    /* Passed STRAIGHT THROUGH from the row the caller supplied. A pricing quote's SKUs have no id
       of their own — they are a position in the submitted payload — so the caller puts that
       position on the row and reads it back here rather than parsing it out of a label somebody
       may translate. Project Approval's rows carry no `index` and are unaffected. */
    index: s2.index,
    /* And its name, for the same reason: a pricing quote's SKU has nothing else to be called, and
       the record it lands in has to read after the quote has moved on. Project Approval ignores
       it — its rows are identified by a real id. */
    name: s2.itemName,
    decision: marks[s2.projectId] === 'Rejected' ? 'Rejected' : 'Approved',
    note: String(reasons[s2.projectId] || '').trim(),
  })), [waiting, marks, reasons]);
  /* WHAT IS BEING DECIDED, BACK TO THE CALLER AS IT CHANGES. Pricing Approval re-prices its cards
     on the SKUs still in and draws the figure under this dialog, so the approver confirms on the
     numbers that will apply rather than on the numbers of an order they are about to cut down.
     The dialog itself stays free of that arithmetic: it hands over the marks and renders whatever
     the caller gives back as `summary`. */
  useEffect(() => { if (onMarks) onMarks(decided); }, [decided, onMarks]);
  const no = decided.filter((d) => d.decision === 'Rejected').length;
  const yes = decided.length - no;
  // A refusal owes a reason, and it is this SKU's own — the group has carried ONE reason for the
  // whole decision, which is exactly why Young's comment could not be read against any one item.
  const unexplained = decided.filter((d) => d.decision === 'Rejected' && !d.note);
  // Resubmitting a project nobody touched is only allowed WITH an explanation — otherwise the
  // approver gets the identical request back and has nothing new to judge.
  const unchanged = isResubmit && !spec.group?.changedSinceDecision;
  const needsNote = rejecting || unchanged;
  const count = bulk ? selectedIds.length : 1;

  const title = isResubmit
    ? t('pa.dlgResubmitTitle', { id: spec.group.groupId })
    : bulk ? t('pa.dlgDecisionBulk', { n: count }) : t('pa.dlgDecisionOne', { id: spec.group.groupId });
  const subtitle = isResubmit
    ? spec.group.groupName
    : bulk ? selectedIds.join(', ') : spec.group.groupName;

  return (
    /* `overlayClass` LIFTS IT OVER ANOTHER OVERLAY. This dialog is z-index 60, which is right
       above a page; opened from inside Pricing Approval's quote panel — a fixed layer at 1200 —
       it would be drawn underneath it and the screen would simply stop responding. The seventh
       time this codebase would have lost a control to something drawn on top of it, so the one
       caller that needs the lift asks for it rather than everybody getting 1300. */
    <div className={`pa-overlay ${overlayClass || ''}`}>
      <div className="pa-modal pa-decision">
        <div className="pa-decision-head">
          <h3>{title}</h3>
          <button type="button" className="pa-decision-x" onClick={onCancel} aria-label={t('xShell.close')}>&times;</button>
        </div>
        <p className="pa-modal-sub">{subtitle}</p>

        {perSku && (
          <div className="pa-skus">
            <div className="pa-skus-head">
              <span>{t('pa.dlgSkuHead', { n: waiting.length })}</span>
              <div className="pa-skus-all">
                {/* "Approve all" is now the undo for every cross — the same outcome it always
                    had, reached by taking the exceptions back off. */}
                <button type="button" className="pa-skubtn ok" onClick={() => setMarks({})}>
                  ✓ {t('pa.dlgAllApprove')}
                </button>
                <button type="button" className="pa-skubtn no"
                  onClick={() => setMarks(Object.fromEntries(waiting.map((s2) => [s2.projectId, 'Rejected'])))}>
                  ✕ {t('pa.dlgAllReject')}
                </button>
              </div>
            </div>
            {/* Already decided in an earlier round, and NOT asked again — what the approver has
                already approved stays approved, which is half the point of deciding per SKU. */}
            {skus.filter((s2) => (s2.approvalStatus || 'Pending') !== 'Pending').map((s2) => (
              <div className={`pa-sku done ${s2.approvalStatus === 'Rejected' ? 'no' : 'ok'}`} key={s2.projectId}>
                <span className="pa-sku-id">{s2.projectId}</span>
                <span className="pa-sku-name">{s2.itemName}</span>
                <span className="pa-sku-was">
                  {s2.approvalStatus === 'Rejected' ? `✕ ${t('pa.dlgWasRejected')}` : `✓ ${t('pa.dlgWasApproved')}`}
                </span>
              </div>
            ))}
            {waiting.map((s2) => {
              const m = marks[s2.projectId] || '';
              return (
                <div className={`pa-sku ${m === 'Rejected' ? 'no' : ''}`} key={s2.projectId}>
                  <span className="pa-sku-id">{s2.projectId}</span>
                  <span className="pa-sku-name">{s2.itemName}</span>
                  {/* The word beside the cross is what carries the state — a button in two shades
                      of itself is not something everybody can read, and in print or forced colours
                      it is nothing at all. */}
                  {/* A CALLER'S OWN MARK ON THE ROW. Pricing Approval puts "not opened yet" here
                      for a SKU the approver never clicked through to — Max, 2026-10-05: "cần mark
                      lại là phiếu chưa mở trong bảng summary luôn nhé". The tab strip behind the
                      overlay says the same thing and cannot be seen from here, which is the whole
                      point: this is where the SKU is agreed to. Project Approval sets none. */}
                  {/* INSIDE the state cell, not beside it. `.pa-sku` is a four-column grid and a
                      fifth child wrapped onto a second line, which put the cross under the row it
                      belongs to. */}
                  <span className="pa-sku-state">
                    {!!s2.flag && <em className="pa-sku-flag">{s2.flag}</em>}
                    {m === 'Rejected' ? t('pa.dlgSkuNo') : t('pa.dlgSkuYes')}
                  </span>
                  <div className="pa-sku-pick">
                    <button type="button" className={`pa-skumark no ${m === 'Rejected' ? 'on' : ''}`}
                      aria-pressed={m === 'Rejected'}
                      title={m === 'Rejected' ? t('pa.dlgSkuUndo') : t('pa.dlgReject')}
                      onClick={() => toggle(s2.projectId)}>✕</button>
                  </div>
                  {m === 'Rejected' && (
                    /* TWO LINES, not one — Max, 2026-09-21. A one-line box asks for a few words,
                       and what belongs here is the sentence the requester will work from; it also
                       hid the start of anything longer as soon as the caret moved to the end. It
                       still grows by hand for a reason that needs more room. */
                    <textarea
                      className="pa-sku-why"
                      rows={2}
                      maxLength={DECISION_MAX}
                      value={reasons[s2.projectId] || ''}
                      placeholder={t('pa.dlgSkuWhy')}
                      onChange={(e) => setReasons((r) => ({ ...r, [s2.projectId]: e.target.value }))}
                    />
                  )}
                </div>
              );
            })}
            <p className="pa-skus-left">{t('pa.dlgSkuDefault')}</p>
          </div>
        )}

        {!isResubmit && !perSku && (
          <div className="pa-decision-row">
            <span className="pa-decision-label">{t('pa.dlgStatus')}</span>
            <div className="pa-radio-group">
              <label className={`pa-radio ${spec.action === 'approve' ? 'on' : ''}`}>
                <input type="radio" checked={spec.action === 'approve'} onChange={() => onSwitch('approve')} />
                {t('pa.dlgAgree')}
              </label>
              <label className={`pa-radio ${rejecting ? 'on reject' : ''}`}>
                <input type="radio" checked={rejecting} onChange={() => onSwitch('reject')} />
                {t('pa.dlgReject')}
              </label>
            </div>
          </div>
        )}

        {/* One box per item, with the approver's own reason above it — the manager is answering
            something specific and should not have to remember what it was. */}
        {!!backSkus.length && (
          <div className="pa-skus">
            <div className="pa-skus-head"><span>{t('pa.dlgBackHead', { n: backSkus.length })}</span></div>
            {backSkus.map((s2) => (
              <div className="pa-sku back" key={s2.projectId}>
                <span className="pa-sku-id">{s2.projectId}</span>
                <span className="pa-sku-name">{s2.itemName}</span>
                <span className="pa-sku-state">{t('pa.dlgBackWas')}</span>
                {!!s2.decisionNote && <div className="pa-sku-was-why">“{s2.decisionNote}”</div>}
                <textarea
                  className="pa-sku-why changed"
                  rows={2}
                  maxLength={DECISION_MAX}
                  value={reasons[s2.projectId] || ''}
                  placeholder={t('pa.dlgBackWhy')}
                  onChange={(e) => setReasons((r) => ({ ...r, [s2.projectId]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        )}

        {/* The caller's own read-back, between the rows and the note box: it is about what the
            rows add up to, so it belongs under them and above the box where the approver explains
            the decision. */}
        {!!summary && <div className="pa-decision-summary">{summary}</div>}

        {unchanged && (
          <div className="pa-warn-box">
            <strong>{t('pa.dlgUnchangedTitle')}</strong> {t('pa.dlgUnchangedBody')}
          </div>
        )}

        <div className="pa-decision-row">
          <span className="pa-decision-label">
            {isResubmit ? t('pa.dlgManagerOpinion') : t('pa.dlgApproverOpinion')}
            {needsNote && <em className="pa-req"> *</em>}
          </span>
          <div className="pa-decision-field">
            <textarea
              rows={5}
              maxLength={DECISION_MAX}
              value={note}
              onChange={(e) => onNote(e.target.value)}
              placeholder={rejecting
                ? t('pa.dlgPhReject')
                : isResubmit
                  ? (unchanged ? t('pa.dlgPhResubmitRequired') : t('pa.dlgPhResubmit'))
                  : t('pa.dlgPhOptional')}
            />
            <span className="pa-counter">{note.length} / {DECISION_MAX}</span>
          </div>
        </div>

        <div className="pa-modal-actions">
          <button type="button" className="pa-btn" onClick={onCancel} disabled={busy}>{t('pa.btnCancel')}</button>
          <button
            type="button"
            className={`pa-btn ${perSku ? (no && !yes ? 'reject' : 'approve')
              : rejecting ? 'reject' : isResubmit ? 'primary' : 'approve'}`}
            disabled={busy
              || (needsNote && !note.trim())
              || (perSku && unexplained.length > 0)}
            onClick={() => onConfirm(perSku ? decided : (isResubmit ? { skuNotes: reasons } : null))}
          >
            {/* THE BUTTON READS BACK THE DECISION before it is pressed. With the outcome being the
                sum of the rows, a button saying only "Approve" would be the screen deciding what
                the approver meant. */}
            {busy ? t('pa.working')
              : isResubmit ? t('pa.btnResubmit')
                : perSku ? (no === 0 ? t('pa.btnApproveSkus', { n: yes })
                  : yes === 0 ? t('pa.btnRejectSkus', { n: no })
                    : t('pa.btnPartial', { yes, no }))
                  : rejecting ? (bulk ? t('pa.btnRejectN', { n: count }) : t('pa.dlgReject'))
                    : (bulk ? t('pa.btnApproveN', { n: count }) : t('pa.btnApprove'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DecisionDialog;
